import { Autocomplete, Chip, createFilterOptions, InputAdornment, TextField, Tooltip } from '@mui/material';
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import type { InputHTMLAttributes, SyntheticEvent } from 'react';
import { useMemo, useRef } from 'react';
import type { FieldValues, RegisterOptions } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';

import type { VNFieldHelpProps } from '@/components/VNFieldHelp';
import {
  hasFieldHelpIcon,
  normalizeFieldHelpProps,
  useVNFieldHelpContext,
  VNFieldHelpIconButton,
  VNFieldHelpItemTipIcon,
  VNFieldHelpLayout,
} from '@/components/VNFieldHelp';

type AutocompleteInputProps = {
  params: AutocompleteRenderInputParams;
  label: string;
  required: boolean;
  error?: boolean;
  helperText?: string;
  inputRef: React.RefObject<HTMLInputElement | null>;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
};

const VNAutocompleteInput = ({ params, label, required, error, helperText, inputRef, inputProps }: AutocompleteInputProps) => {
  const { iconConfig } = useVNFieldHelpContext();
  const { onPaste: muiOnPaste, ...restMuiInputProps } = params.inputProps ?? {};
  const { onPaste: callerOnPaste, ...restCallerInputProps } = inputProps ?? {};

  return (
    <TextField
      {...params}
      label={`${label}${required ? ' *' : ''}`}
      size="small"
      error={error}
      helperText={helperText}
      inputRef={inputRef}
      inputProps={{
        ...restMuiInputProps,
        ...restCallerInputProps,
        ...(callerOnPaste || muiOnPaste
          ? {
              onPaste: (e) => {
                callerOnPaste?.(e);
                if (!e.defaultPrevented) {
                  muiOnPaste?.(e);
                }
              },
            }
          : {}),
      }}
      InputProps={{
        ...params.InputProps,
        endAdornment: iconConfig ? (
          <>
            {params.InputProps.endAdornment}
            <InputAdornment position="end" className="vn-field-help__input-adornment">
              <VNFieldHelpIconButton {...iconConfig} />
            </InputAdornment>
          </>
        ) : (
          params.InputProps.endAdornment
        ),
      }}
    />
  );
};

type Props = VNFieldHelpProps & {
  name: string;
  label: string;
  labelProp?: string;
  valueProp?: string;
  options?: any[];
  hideOptions?: boolean;
  multiple?: boolean;
  rules?: Omit<RegisterOptions<FieldValues, string>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'>;
  required?: boolean;
  disabled?: boolean;
  allowClear?: boolean;
  onChange?: (value: any) => void;
  optionTemplate?: (option: any) => React.ReactNode;
  inputValue?: string;
  onInputChange?: (event: SyntheticEvent, value: string, reason: string) => void;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  allowCustom?: boolean;
  chipTooltip?: boolean;
  chipTooltipArrow?: boolean;
  chipTooltipEnterDelay?: number;
  chipTooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
};

const VNAutocomplete = ({
  name,
  label,
  labelProp = 'label',
  valueProp = 'value',
  options = [],
  hideOptions = false,
  multiple = false,
  rules,
  required = false,
  disabled = false,
  allowClear = false,
  onChange = null,
  optionTemplate,
  inputValue,
  onInputChange,
  inputProps,
  allowCustom = false,
  chipTooltip = true,
  chipTooltipArrow = true,
  chipTooltipEnterDelay = 500,
  chipTooltipPlacement = 'top',
  ...fieldHelpProps
}: Props) => {
  const { control, getValues } = useFormContext();

  const normalized = normalizeFieldHelpProps(fieldHelpProps);
  const { dropDownItemHelperTooltip, dropDownItemHelperText } = normalized;
  const showFieldHelp = hasFieldHelpIcon(fieldHelpProps);
  const hasItemHelp = Boolean(
    (dropDownItemHelperTooltip && Object.keys(dropDownItemHelperTooltip).length > 0) ||
      (dropDownItemHelperText && Object.keys(dropDownItemHelperText).length > 0)
  );

  const selectedValues: string[] | string = (() => {
    const currentValue = getValues(name);
    if (multiple) {
      return Array.isArray(currentValue) ? currentValue : [];
    }
    return (currentValue as string) || '';
  })();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const optionValues = useMemo(() => {
    const predefinedValues =
      typeof options?.[0] === 'object' ? options.map((option) => option[valueProp] ?? option.value) : options;

    return Array.from(new Set([...predefinedValues, ...(Array.isArray(selectedValues) ? selectedValues : [selectedValues])]));
  }, [options, selectedValues, valueProp]);

  const optionLabels = useMemo(() => {
    const predefinedLabels =
      typeof options?.[0] === 'object'
        ? Object.fromEntries(options.map((option) => [option[valueProp] ?? option.value, option[labelProp] ?? option.label]))
        : Object.fromEntries(options.map((option) => [option, option]));

    if (Array.isArray(selectedValues)) {
      selectedValues.forEach((val) => {
        if (!predefinedLabels[val]) {
          predefinedLabels[val] = val;
        }
      });
    } else if (selectedValues && !predefinedLabels[selectedValues]) {
      predefinedLabels[selectedValues] = selectedValues;
    }

    return predefinedLabels;
  }, [options, selectedValues, labelProp, valueProp]);

  const optionsByValue = useMemo(() => {
    if (!optionTemplate || typeof options?.[0] !== 'object') {
      return {};
    }
    return Object.fromEntries(options.map((option) => [option[valueProp] ?? option.value, option]));
  }, [options, optionTemplate, valueProp]);

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: any) => optionLabels[option] || '',
  });

  const renderOptionContent = (option: string | number) => {
    const displayLabel =
      optionTemplate && optionsByValue[option] ? optionTemplate(optionsByValue[option]) : optionLabels[option] ?? '';
    const key = String(option);
    const itemTooltip = dropDownItemHelperTooltip?.[key];
    const itemHelperText = dropDownItemHelperText?.[key];

    if (hasItemHelp) {
      return (
        <div className="vn-field-help-menu-item__content">
          <div className="vn-field-help-menu-item__main">
            <span className="vn-field-help-menu-item__label">{displayLabel}</span>
            {itemTooltip && <VNFieldHelpItemTipIcon tooltip={itemTooltip} />}
          </div>
          {itemHelperText && <span className="vn-field-help-menu-item__subtext">{itemHelperText}</span>}
        </div>
      );
    }

    return displayLabel;
  };

  const controller = (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required ? `${label} is required` : undefined,
        ...rules,
      }}
      render={({ field: { value, onChange: innerOnChange }, fieldState }) => {
        const hasValue = multiple ? Array.isArray(value) && value.length > 0 : value != null && value !== '';

        return (
          <Autocomplete
            disableCloseOnSelect={multiple}
            multiple={multiple}
            freeSolo={allowCustom || hideOptions}
            disableClearable={!allowClear || !hasValue}
            options={hideOptions ? [] : optionValues}
            disabled={disabled}
            {...(inputValue !== undefined ? { inputValue } : {})}
            onInputChange={(event, newInputValue, reason) => {
              onInputChange?.(event, newInputValue, reason);

              if (allowCustom && !multiple && (reason === 'input' || reason === 'clear')) {
                innerOnChange(newInputValue);
              }
            }}
            value={(() => {
              if (multiple) {
                return Array.isArray(value) ? value : [];
              }
              return value || '';
            })()}
            onChange={(_, newValue) => {
              if (onChange) {
                onChange(newValue);
                return;
              }
              if (!multiple) {
                innerOnChange(newValue);
              } else {
                const cleanedValue = Array.from(new Set(newValue.map((v: string) => v.trim()).filter(Boolean)));
                innerOnChange(cleanedValue);
              }
            }}
            filterOptions={hideOptions ? () => [] : filterOptions}
            renderOption={(props, option) => {
              const { key: reactKey, onClick, ...restProps } = props;
              return (
                <li
                  {...restProps}
                  key={reactKey}
                  onClick={(e) => {
                    if ((e.target as Element).closest('.vn-field-help-menu-item__icon-wrap')) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    onClick?.(e);
                  }}
                  className={[restProps.className, hasItemHelp ? 'vn-field-help-menu-item' : ''].filter(Boolean).join(' ')}
                >
                  {renderOptionContent(option)}
                </li>
              );
            }}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => {
                const label = optionLabels[option] ?? '';
                const chip = (
                  <Chip
                    {...getTagProps({ index })}
                    classes={{ root: 'vn-chip' }}
                    size="small"
                    key={option}
                    label={label}
                  />
                );

                if (!chipTooltip || !label) {
                  return chip;
                }

                return (
                  <Tooltip
                    key={`tooltip-${option}`}
                    title={label}
                    arrow={chipTooltipArrow}
                    placement={chipTooltipPlacement}
                    enterDelay={chipTooltipEnterDelay}
                  >
                    <span>{chip}</span>
                  </Tooltip>
                );
              })
            }
            getOptionLabel={(option) => optionLabels[option] ?? String(option ?? '')}
            renderInput={(params) => (
              <VNAutocompleteInput
                params={params}
                label={label}
                required={required}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                inputRef={inputRef}
                inputProps={inputProps}
              />
            )}
          />
        );
      }}
    />
  );

  return (
    <div className="vn-autocomplete">
      {showFieldHelp ? (
        <VNFieldHelpLayout
          label={label}
          required={required}
          iconAttach="adornment"
          className="vn-field-help--autocomplete"
          {...fieldHelpProps}
        >
          {controller}
        </VNFieldHelpLayout>
      ) : (
        controller
      )}
    </div>
  );
};

export default VNAutocomplete;
